import { jsPDF } from 'jspdf'
import autoTable from 'jspdf-autotable'
import * as XLSX from 'xlsx'
import { baseName } from './format'
import { getPdfjs } from './pdfTools'

const XLSX_MIME = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
const PPTX_MIME = 'application/vnd.openxmlformats-officedocument.presentationml.presentation'

// ---------- Excel → PDF ----------
export async function excelToPdf(file) {
  const buf = await file.arrayBuffer()
  const wb = XLSX.read(buf, { type: 'array' })
  const doc = new jsPDF({ orientation: 'landscape', unit: 'pt', format: 'a4' })
  let first = true

  wb.SheetNames.forEach((name) => {
    const ws = wb.Sheets[name]
    const rows = XLSX.utils.sheet_to_json(ws, { header: 1, raw: false, defval: '' })
    if (!first) doc.addPage()
    first = false
    doc.setFontSize(13)
    doc.text(name, 40, 34)
    if (rows.length) {
      autoTable(doc, {
        head: [rows[0].map((c) => (c == null ? '' : String(c)))],
        body: rows.slice(1).map((r) => r.map((c) => (c == null ? '' : String(c)))),
        startY: 46,
        styles: { fontSize: 8, cellPadding: 4, overflow: 'linebreak' },
        headStyles: { fillColor: [79, 70, 229] },
        margin: { left: 40, right: 40 },
      })
    } else {
      doc.setFontSize(10)
      doc.text('(empty sheet)', 40, 60)
    }
  })

  const blob = doc.output('blob')
  return { blob, name: `${baseName(file.name)}.pdf`, mime: 'application/pdf' }
}

// ---------- PDF → Excel ----------
// Group text items into rows (by y) and cells (by x order) per page → a sheet.
export async function pdfToExcel(file) {
  const lib = await getPdfjs()
  const data = await file.arrayBuffer()
  const pdf = await lib.getDocument({ data }).promise
  const wb = XLSX.utils.book_new()

  for (let p = 1; p <= pdf.numPages; p++) {
    const page = await pdf.getPage(p)
    const content = await page.getTextContent()
    const items = content.items
      .filter((it) => it.str.trim() !== '')
      .map((it) => ({ str: it.str, x: it.transform[4], y: it.transform[5] }))

    // bucket by row (y), tolerance based on typical line height
    const rowsMap = []
    const TOL = 4
    items.forEach((it) => {
      let row = rowsMap.find((r) => Math.abs(r.y - it.y) <= TOL)
      if (!row) {
        row = { y: it.y, cells: [] }
        rowsMap.push(row)
      }
      row.cells.push(it)
    })
    rowsMap.sort((a, b) => b.y - a.y) // top to bottom (PDF y grows upward)
    const aoa = rowsMap.map((r) => r.cells.sort((a, b) => a.x - b.x).map((c) => c.str))

    const ws = XLSX.utils.aoa_to_sheet(aoa.length ? aoa : [['']])
    XLSX.utils.book_append_sheet(wb, ws, `Page ${p}`.slice(0, 31))
  }

  const out = XLSX.write(wb, { type: 'array', bookType: 'xlsx' })
  return { blob: new Blob([out], { type: XLSX_MIME }), name: `${baseName(file.name)}.xlsx`, mime: XLSX_MIME }
}

// pptxgenjs' bundled build imports `node:fs`, which CRA's webpack can't bundle,
// so load the self-contained browser build at runtime instead.
let pptxPromise = null
function loadPptxGen() {
  if (typeof window !== 'undefined' && window.PptxGenJS) return Promise.resolve(window.PptxGenJS)
  if (pptxPromise) return pptxPromise
  pptxPromise = new Promise((resolve, reject) => {
    const s = document.createElement('script')
    s.src = 'https://cdn.jsdelivr.net/npm/pptxgenjs@4.0.1/dist/pptxgen.bundle.js'
    s.onload = () => resolve(window.PptxGenJS)
    s.onerror = () => reject(new Error('Could not load the PowerPoint library (check your connection).'))
    document.head.appendChild(s)
  })
  return pptxPromise
}

// ---------- PDF → PPT ----------
// Render each page to an image and place it on a slide (visual fidelity kept).
export async function pdfToPpt(file) {
  const PptxGenJS = await loadPptxGen()
  const lib = await getPdfjs()
  const data = await file.arrayBuffer()
  const pdf = await lib.getDocument({ data }).promise

  const pptx = new PptxGenJS()
  // Layout from the first page aspect.
  const first = await pdf.getPage(1)
  const v1 = first.getViewport({ scale: 1 })
  const layoutW = 10
  const layoutH = +(layoutW * (v1.height / v1.width)).toFixed(2)
  pptx.defineLayout({ name: 'PAGE', width: layoutW, height: layoutH })
  pptx.layout = 'PAGE'

  for (let p = 1; p <= pdf.numPages; p++) {
    const page = await pdf.getPage(p)
    const viewport = page.getViewport({ scale: 2 })
    const canvas = document.createElement('canvas')
    canvas.width = viewport.width
    canvas.height = viewport.height
    const ctx = canvas.getContext('2d')
    ctx.fillStyle = '#fff'
    ctx.fillRect(0, 0, canvas.width, canvas.height)
    await page.render({ canvasContext: ctx, viewport }).promise
    const dataUrl = canvas.toDataURL('image/jpeg', 0.9)
    const slide = pptx.addSlide()
    slide.addImage({ data: dataUrl, x: 0, y: 0, w: layoutW, h: layoutH })
  }

  const blob = await pptx.write({ outputType: 'blob' })
  return { blob, name: `${baseName(file.name)}.pptx`, mime: PPTX_MIME }
}

// ---------- PPT → PDF ----------
// .pptx: extract each slide's text and lay it out one slide per page.
// .ppt (legacy binary): recover readable text heuristically and paginate it.
export async function pptToPdf(file) {
  const name = (file.name || '').toLowerCase()
  const buf = await file.arrayBuffer()
  const doc = new jsPDF({ orientation: 'landscape', unit: 'pt', format: 'a4' })
  const pw = doc.internal.pageSize.getWidth()
  const ph = doc.internal.pageSize.getHeight()
  const margin = 48

  if (name.endsWith('.ppt')) {
    const text = extractPptText(buf)
    if (!text) throw new Error('Could not read text from this .ppt file. Please save it as .pptx.')
    doc.setFontSize(12)
    doc.setTextColor(20)
    let y = margin
    doc.splitTextToSize(text, pw - margin * 2).forEach((line) => {
      if (y > ph - margin) {
        doc.addPage()
        y = margin
      }
      doc.text(line, margin, y)
      y += 16
    })
    const blob = doc.output('blob')
    return { blob, name: `${baseName(file.name)}.pdf`, mime: 'application/pdf' }
  }

  // .pptx (Open XML zip)
  const JSZip = (await import('jszip')).default
  const zip = await JSZip.loadAsync(buf)
  const slideNames = Object.keys(zip.files)
    .filter((n) => /^ppt\/slides\/slide\d+\.xml$/.test(n))
    .sort((a, b) => parseInt(a.match(/(\d+)/)[1], 10) - parseInt(b.match(/(\d+)/)[1], 10))

  if (!slideNames.length) throw new Error('No slides found — is this a valid PowerPoint file?')

  for (let i = 0; i < slideNames.length; i++) {
    const xml = await zip.file(slideNames[i]).async('string')
    const texts = [...xml.matchAll(/<a:t>([\s\S]*?)<\/a:t>/g)].map((m) => decodeXml(m[1])).filter((t) => t.trim())
    if (i > 0) doc.addPage()

    doc.setFontSize(10)
    doc.setTextColor(150)
    doc.text(`Slide ${i + 1}`, margin, 32)
    doc.setTextColor(20)

    let y = 70
    texts.forEach((t, idx) => {
      const size = idx === 0 ? 22 : 13
      doc.setFontSize(size)
      doc.splitTextToSize(t, pw - margin * 2).forEach((line) => {
        if (y > ph - margin) {
          doc.addPage()
          y = margin
        }
        doc.text(line, margin, y)
        y += size * 1.4
      })
      y += 6
    })
    if (!texts.length) {
      doc.setFontSize(12)
      doc.text('(no text on this slide)', margin, 70)
    }
  }

  const blob = doc.output('blob')
  return { blob, name: `${baseName(file.name)}.pdf`, mime: 'application/pdf' }
}

// Recover readable text from a legacy binary .ppt (OLE) file — text is stored
// as Windows-1252 / UTF-16LE, so pull out long printable runs.
function extractPptText(buf) {
  const bytes = new Uint8Array(buf)
  const runs = []
  let run = ''
  for (let i = 0; i < bytes.length; i++) {
    const b = bytes[i]
    if (b === 0) continue // UTF-16LE padding
    if (b === 10 || b === 13 || (b >= 32 && b <= 126) || (b >= 0xa0 && b <= 0xff)) {
      run += String.fromCharCode(b)
    } else {
      if (run.trim().length >= 4) runs.push(run.trim())
      run = ''
    }
  }
  if (run.trim().length >= 4) runs.push(run.trim())

  const good = runs.filter((r) => {
    const letters = (r.match(/[A-Za-z]/g) || []).length
    return letters >= 4 && letters / r.length > 0.35
  })
  return (good.length ? good : runs)
    .join('\n')
    .replace(/[ \t]{2,}/g, ' ')
    .replace(/\n{3,}/g, '\n\n')
    .trim()
}

function decodeXml(s) {
  return s
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'")
    .replace(/&apos;/g, "'")
    .replace(/&amp;/g, '&')
}
